<!-- eslint-disable vue/multi-word-component-names -->
<!-- eslint-disable no-undef -->
<script setup>
  import { useStore } from 'vuex';
  import { ref, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { auth } from '@/firebase';

  import DropdownProfile from '@/components/dropdowns/Profile';
  import NotificationBell from '@/components/common/NotificationBell';
  import { useThemeMode } from '@/composables/useThemeMode';

  const { currentTheme, toggleTheme } = useThemeMode();

  defineOptions({ name: 'DefaultHeader' })

  const store = useStore();
  const router = useRouter();

  const user = computed(() => store.state.user);
  const companyLogo = ref('');
  const companyName = ref('');
  const isProfileMenuOpen = ref(false);

  if (user.value && user.value.entityData && user.value.entityViewing &&
      user.value.entityViewing.length > 0 && user.value.entityData[user.value.entityViewing[0]]) {
    if (user.value.entityViewing.length > 1) {
      companyName.value = 'All';
      companyLogo.value = null;
    } else {
      companyName.value = user.value.entityData[user.value.entityViewing[0]].name || 'Meshportal';
      companyLogo.value = user.value.entityData[user.value.entityViewing[0]].logo || null;
    }
  } else {
    companyName.value = 'Meshportal';
    companyLogo.value = null;
  }

  const toggleProfileMenu = () => {
    isProfileMenuOpen.value = !isProfileMenuOpen.value;
  };

  const signOut = async () => {
    await auth.signOut();
    store.dispatch('setUser', null);
    router.push({ name: 'Login' });
  };

  const handlePWAInstallRequest = () => {
    window.dispatchEvent(new CustomEvent('trigger-pwa-install'));
  };
</script>

<template>
  <header class="sticky top-0 z-20 w-full bg-mc-ink text-mc-white border-b border-line-on-dark">
    <!-- Brand stripe -->
    <div class="h-1 bg-mc-red"></div>

    <div class="flex items-center gap-4 pl-3 pr-section-x min-h-[80px]">

      <!-- Left: mobile hamburger + company identity -->
      <div class="flex items-center gap-4 min-w-0 flex-1">
        <button
          @click="$emit('toggle-mobile-menu')"
          class="md:hidden flex-shrink-0 p-2 rounded-mc-sm text-on-dark hover:bg-white/10 transition-colors duration-160"
          aria-label="Open navigation"
        >
          <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <div
          v-if="companyLogo"
          class="flex-shrink-0 flex items-center px-3"
        >
          <img class="h-16 w-auto object-contain block" :src="companyLogo" :alt="`${companyName} logo`" />
        </div>

        <span class="font-display font-bold text-[clamp(1.2rem,1.6vw,1.6rem)] text-mc-white truncate leading-none">
          {{ companyName }}
        </span>
      </div>

      <!-- Right: actions -->
      <div class="flex items-center gap-9 flex-shrink-0">
        <NotificationBell @install-pwa-requested="handlePWAInstallRequest" />

        <!-- Dark / light mode toggle -->
        <button
          @click="toggleTheme"
          class="flex items-center justify-center w-9 h-9 rounded-full text-on-dark hover:bg-white/10 transition-colors duration-160"
          :aria-label="currentTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'"
        >
          <!-- Sun (shown in dark mode) -->
          <svg v-if="currentTheme === 'dark'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
          </svg>
          <!-- Moon (shown in light mode) -->
          <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
          </svg>
        </button>

        <!-- Desktop: full profile dropdown -->
        <DropdownProfile
          v-if="user"
          class="hidden md:flex"
          :username="user.firstName"
          :userEmail="user.email"
        />

        <!-- Mobile: avatar button -->
        <button
          type="button"
          @click="toggleProfileMenu"
          class="md:hidden flex items-center justify-center w-10 h-10 rounded-full ring-2 ring-white/25 hover:ring-white/60 transition-all duration-160 overflow-hidden"
          aria-label="Open profile menu"
        >
          <img
            v-if="user && user.profilePic"
            :src="user.profilePic"
            alt="Profile"
            class="w-full h-full object-cover"
          />
          <img
            v-else
            src="@/assets/images/default-avatar.png"
            alt="Profile"
            class="w-full h-full object-cover"
          />
        </button>
      </div>
    </div>

    <!-- Mobile profile panel -->
    <div v-if="isProfileMenuOpen" class="md:hidden fixed inset-0 z-50">
      <div class="fixed inset-0 bg-mc-ink/80" @click="isProfileMenuOpen = false"></div>
      <div class="fixed right-4 top-[76px] w-52 bg-mc-charcoal rounded-mc-md shadow-mc-md border border-line-on-dark overflow-hidden">
        <div class="px-4 py-3 border-b border-line-on-dark">
          <p class="font-mono text-[0.65rem] uppercase tracking-[0.12em] text-on-dark-muted mb-1">Signed in as</p>
          <p v-if="user" class="font-body font-semibold text-sm text-mc-white truncate">{{ user.email }}</p>
        </div>
        <a
          href="#"
          @click.prevent="signOut"
          class="flex items-center px-4 py-3 font-body text-sm text-on-dark hover:bg-white/10 transition-colors duration-160 no-underline"
        >
          Sign out
        </a>
      </div>
    </div>
  </header>
</template>
